import { useState, useEffect } from 'react';
import { Button } from '@/components/tailwind/button';
import { Text } from '@/components/tailwind/text';
import { useAuth } from '@/context/use-auth';
import { getNDAReminderDays, updateNDAReminderDays } from '@/services/member-consent-services';
import ValidationAlert from '@/components/validation-alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

export default function SectionNdaReminderSettings() {
    const { token } = useAuth();
    const [reminderDays, setReminderDays] = useState(30);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showHint, setShowHint] = useState(false);

    // Load current configuration from backend
    useEffect(() => {
        async function loadConfiguration() {
            if (!token) return;
            const days = await getNDAReminderDays(token);
            setReminderDays(days);
        }
        
        loadConfiguration();
    }, [token]);

    async function handleSave() {
        if (!token) return;
        
        setIsLoading(true);
        setError(null);
        setIsSaved(false);
        
        const success = await updateNDAReminderDays(token, reminderDays);
        
        if (success) {
            setIsSaved(true);
            setTimeout(() => setIsSaved(false), 3000);
        } else {
            setError('Fehler beim Speichern der Konfiguration');
        }
        
        setIsLoading(false);
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle>Verschwiegenheitserklärung (NDA) Erinnerungen</CardTitle>
                    <button
                        onClick={() => setShowHint(!showHint)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                        aria-label="Hilfe anzeigen"
                        data-testid="nda-reminder-help-toggle"
                    >
                        <QuestionMarkCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>
                <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Konfigurieren Sie, nach wie vielen Tagen der Mitgliedschaft eine Erinnerung für fehlende Verschwiegenheitserklärungen angezeigt werden soll.
                </Text>
            </CardHeader>
            
            <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                    <Text className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Erinnerung anzeigen nach
                    </Text>
                    <input
                        id="nda-reminder-days"
                        type="number"
                        min="1"
                        max="999"
                        value={reminderDays}
                        onChange={(e) => setReminderDays(parseInt(e.target.value) || 30)}
                        className="w-16 px-2 py-1 text-sm border rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={isLoading}
                        data-testid="nda-reminder-days-input"
                    />
                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                        Tagen nach Mitgliedschaftsbeginn
                    </Text>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading}
                        className="ml-auto"
                        data-testid="nda-reminder-save-button"
                    >
                        {isLoading ? 'Speichern...' : 'Speichern'}
                    </Button>
                </div>

                {showHint && (
                    <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4" data-testid="nda-reminder-help-text">
                        <Text className="text-sm text-blue-800 dark:text-blue-200">
                            Diese Einstellung betrifft nur aktive Mitglieder. 
                            Das Dashboard zeigt eine Warnung für alle aktiven Mitglieder, die länger als die 
                            konfigurierte Anzahl von Tagen Mitglied sind und noch keine Verschwiegenheitserklärung 
                            unterschrieben haben.
                        </Text>
                    </div>
                )}

                {error && (
                    <div data-testid="nda-reminder-error-message">
                        <ValidationAlert message={error} />
                    </div>
                )}

                {isSaved && (
                    <div className="p-3 bg-green-50 dark:bg-green-950 border-l-4 border-green-400 dark:border-green-500" data-testid="nda-reminder-success-message">
                        <Text className="text-sm text-green-800 dark:text-green-200">
                            Einstellungen erfolgreich gespeichert
                        </Text>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}